import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "service/api";
import {
  GroupInvoiceRequestDto,
  OfferListEntryDto,
} from "library/types/types";

/**
 * Error text of a failed BFF call: ApiError objects carry `message`, FluentValidation
 * failures come back as an array of { FormatedMessage / message } items.
 */
export const getGroupInvoiceErrorMessage = (
  error: any,
  fallback: string,
): string => {
  const data = error?.response?.data;
  if (Array.isArray(data) && data.length > 0) {
    const first = data[0];
    return (
      first?.FormatedMessage ||
      first?.formatedMessage ||
      first?.message ||
      first?.Message ||
      fallback
    );
  }
  return data?.message || data?.Message || error?.message || fallback;
};

/**
 * POST /invoices/group - one invoice document covering the line invoices of several
 * offers of the same payer. Returns the updated offers, each carrying Invoice.GroupInvoice.
 */
export function useCreateGroupInvoice() {
  const { t } = useTranslation();

  return useMutation<OfferListEntryDto[], Error, GroupInvoiceRequestDto>({
    mutationFn: async (requestData: GroupInvoiceRequestDto) => {
      const response = await api.post("/invoices/group", requestData);
      return response.data;
    },
    onSuccess: () => {
      toast.success(
        t("qtg.groupInvoice.createSuccess", "Group invoice created successfully"),
      );
    },
    onError: (error: any) => {
      toast.error(
        getGroupInvoiceErrorMessage(
          error,
          t(
            "qtg.groupInvoice.createError",
            "Failed to create group invoice. Please try again.",
          ),
        ),
      );
    },
  });
}
